/* Profitability — a base case, what moves it, and one variable swept.
 *
 * Every slider change recomputes on the server. That is a round trip per
 * drag, but the sweep alone is 41 full model evaluations and the tornado is
 * another 20-odd, so doing it here would mean keeping a second copy of the
 * model in TypeScript — two implementations that would drift apart the
 * first time a cost line changed. The call is debounced instead.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { profitability } from '../lib/api'
import type {
  ComputeResponse, CottonLot, Market, ProfitInputs, Product, Scenario, SinglePricing, VarMeta,
} from '../lib/api'
import { useAsync } from '../lib/useAsync'
import {
  Blueprint, ErrorBox, KpiRow, Loading, NumberField, Note, PanelHead, Segmented, Slider,
} from '../components/ui'
import { Sweep, Tornado } from '../components/charts/Sensitivity'
import { big, byFmt, crore, inr, kg, num, pct } from '../lib/format'
import {
  CottonCell, CottonPanel, CountSelect, GpsCell, MarketLine, SinglePrices, YarnCell,
} from './profitability/Prices'
import {
  MANUAL_SINGLE, adoptSaved, findCount, lotForGrade, resolveInputs, resolveProducts,
  seedMix, seedSingle,
} from './profitability/pricing'

type Mode = 'single' | 'mix'
type Period = 'day' | 'month'

const R: React.CSSProperties = { textAlign: 'right' }
const DEBOUNCE_MS = 120

export default function Profitability() {
  const base = useAsync(() => profitability.defaults(), [])
  // The market is a convenience, not a dependency: if it cannot be read the
  // page still opens, on typed prices.
  const mkt = useAsync(() => profitability.market(), [])
  const market: Market | null = mkt.data

  const [mode, setMode] = useState<Mode>('single')
  const [period, setPeriod] = useState<Period>('day')
  const [inputs, setInputs] = useState<ProfitInputs | null>(null)
  const [products, setProducts] = useState<Product[] | null>(null)
  // Every cotton the mix is spun from. Products point at one by id, so a
  // cotton's price is entered once however many counts draw on it.
  const [lots, setLots] = useState<CottonLot[]>([])
  // The single-product case's count and grade, and whether each of its two
  // prices is following the market.
  const [single, setSingleState] = useState<SinglePricing>(MANUAL_SINGLE)
  const [oh, setOh] = useState(45_000_000)
  // Overhead by head. This is the input now; `oh` is kept only as the total
  // the tornado and sweep still walk.
  const [ohParts, setOhParts] = useState<Record<string, number> | null>(null)
  const [util, setUtil] = useState(95)
  // Split from utilization: machine hours run vs output while running.
  const [eff, setEff] = useState(85)
  // Yarn price is where the argument usually starts, so it is the opening
  // sweep rather than whichever driver happens to be first.
  const [axis, setAxis] = useState<string | null>('yarn')
  const [seq, setSeq] = useState(5)

  // Saved scenarios live on the server rather than in localStorage, so a
  // base case survives a different browser or machine.
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [savedSig, setSavedSig] = useState<string | null>(null)
  const [selected, setSelected] = useState<string>('')
  const [saveMsg, setSaveMsg] = useState('')

  const [out, setOut] = useState<ComputeResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const timer = useRef<number | null>(null)

  // Seed the editable state once the server has told us the plant defaults.
  useEffect(() => {
    if (base.data && !inputs && !mkt.loading) {
      const mix = seedMix(base.data.mix, mkt.data)
      setInputs(base.data.inputs)
      setProducts(mix.products)
      setLots(mix.lots)
      setSingleState(seedSingle(base.data.single, mkt.data))
      setOh(base.data.inputs.oh)
      setUtil(base.data.inputs.util)
      setEff(base.data.inputs.eff)
      setOhParts(Object.fromEntries(
        base.data.overhead_heads.map((h) => [h.id, h.value])))
    }
  }, [base.data, inputs, mkt.loading, mkt.data])

  // What the model is actually run on: the typed figures with the market's
  // laid over wherever a price is following it.
  const rInputs = useMemo(
    () => (inputs ? resolveInputs(inputs, single, market) : null), [inputs, single, market])
  const rProducts = useMemo(
    () => (products ? resolveProducts(products, lots, market) : null), [products, lots, market])

  useEffect(() => {
    if (!rInputs || !rProducts) return
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      profitability.compute({
        mode, period, inputs: rInputs, products: rProducts, oh, util, eff, axis,
        overhead: ohParts ?? undefined,
      })
        .then((r) => { setOut(r); setErr(null) })
        .catch((e) => setErr(e.message ?? String(e)))
    }, DEBOUNCE_MS)
    return () => { if (timer.current) window.clearTimeout(timer.current) }
  }, [mode, period, rInputs, rProducts, oh, util, eff, axis, ohParts])

  const loadScenarios = useCallback(() => {
    profitability.scenarios().then(setScenarios).catch(() => { /* list is optional */ })
  }, [])
  useEffect(loadScenarios, [loadScenarios])

  // Everything that defines a base case. Compared against the last save so
  // the button can say whether there is anything to save.
  const snapshot = useMemo(
    () => JSON.stringify({ mode, inputs, products, util, eff, ohParts, lots, single }),
    [mode, inputs, products, util, eff, ohParts, lots, single])
  const isSaved = savedSig !== null && savedSig === snapshot

  /** Next free "Case N". Saving upserts by name, so a fresh case has to
   *  claim an unused one — counting the list is not enough once any have
   *  been deleted. */
  const nextCaseName = useCallback(() => {
    const used = new Set(scenarios.map((sc) => sc.name))
    let n = 1
    while (used.has(`Case ${n}`)) n += 1
    return `Case ${n}`
  }, [scenarios])

  const save = useCallback(async () => {
    const name = nextCaseName()
    try {
      await profitability.save({
        name, mode,
        // The prices in force are saved as the typed figures, so a price
        // that is following the market still has a number to fall back to.
        payload: { inputs: rInputs, products: rProducts, util, eff, ohParts, lots, single },
      })
      setSavedSig(snapshot)
      setSelected(name)
      setSaveMsg(`Saved as ${name}.`)
      loadScenarios()
    } catch (e: any) {
      setSaveMsg(e?.message ?? String(e))
    }
  }, [mode, rInputs, rProducts, util, eff, ohParts, lots, single, snapshot, loadScenarios,
    nextCaseName])

  const restore = useCallback((sc: Scenario) => {
    const pl = sc.payload ?? {}
    if (pl.inputs) setInputs(pl.inputs)
    if (pl.products) {
      const mix = adoptSaved(pl.products, pl.lots)
      setProducts(mix.products)
      setLots(mix.lots)
    }
    // A case saved before prices could follow the market has only typed ones.
    setSingleState(pl.single ?? MANUAL_SINGLE)
    if (pl.util != null) setUtil(pl.util)
    if (pl.eff != null) setEff(pl.eff)
    if (pl.ohParts) setOhParts(pl.ohParts)
    setMode(sc.mode === 'mix' ? 'mix' : 'single')
    setSelected(sc.name)
    setSaveMsg(`Loaded ${sc.name}.`)
  }, [])

  const setVar = useCallback((id: keyof ProfitInputs, v: number) => {
    setInputs((cur) => (cur ? { ...cur, [id]: v } : cur))
    // Dragging a price is typing a price: it stops following the market.
    if (id === 'yarn') setSingleState((cur) => ({ ...cur, yarnAuto: false }))
    if (id === 'candy') setSingleState((cur) => ({ ...cur, cottonAuto: false }))
  }, [])

  const setSingle = useCallback((patch: Partial<SinglePricing>) => {
    // Leaving the market starts from the market's figure, so the slider
    // does not jump back to whatever was typed before.
    if (patch.yarnAuto === false && rInputs) {
      setInputs((cur) => (cur ? { ...cur, yarn: rInputs.yarn } : cur))
    }
    if (patch.cottonAuto === false && rInputs) {
      setInputs((cur) => (cur ? { ...cur, candy: rInputs.candy } : cur))
    }
    setSingleState((cur) => ({ ...cur, ...patch }))
  }, [rInputs])

  const setProduct = useCallback((id: string, patch: Partial<Product>) => {
    setProducts((cur) => cur?.map((p) => (p.id === id ? { ...p, ...patch } : p)) ?? cur)
  }, [])

  const setLot = useCallback((id: string, patch: Partial<CottonLot>) => {
    setLots((cur) => cur.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }, [])

  /** Point a row at a count: its name, its yarn price and its cotton all
   *  follow, with the cotton added to the list if the mix has none of it. */
  const pickCount = useCallback((id: string, count: number, quality: string) => {
    const mc = findCount(market, count, quality)
    const row = products?.find((p) => p.id === id)
    if (!mc || !row) return
    const got = lotForGrade(lots, market, mc.grade, row.candy)
    setLots(got.lots)
    setProduct(id, {
      count, quality, name: mc.name, cotton: got.id, yarnAuto: Boolean(mc.yarn),
    })
  }, [market, products, lots, setProduct])

  if (base.error) return <main style={MAIN_STYLE}><ErrorBox error={base.error} onRetry={base.reload} /></main>
  if (!base.data || !inputs || !products) return <main style={MAIN_STYLE}><Loading what="the model" /></main>

  if (!rInputs || !rProducts) return <main style={MAIN_STYLE}><Loading what="the model" /></main>

  const isMix = mode === 'mix'
  const per = period === 'month' ? '/ month' : '/ day'
  const scale = period === 'month' ? 30 : 1
  const R_ = out?.result

  return (
    <>
      <Subline>
        {isMix
          ? 'Run several counts side by side, each with its own price, cotton and spindle output'
          : 'Set a base case, then see which input moves profit hardest'}
      </Subline>

      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 20, flexWrap: 'wrap', padding: '14px 32px',
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <Segmented<Mode> value={mode} minWidth={170}
          // Cotton is the dominant driver in the mix — it opens on the
          // variable the tornado ranks first, rather than on utilization.
          onChange={(m) => { setMode(m); setAxis(m === 'mix' ? 'g:candy' : 'yarn') }}
          options={[
            { value: 'single', label: 'Single product' },
            { value: 'mix', label: 'Product mix' },
          ]} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <ScenarioBar
            scenarios={scenarios} isSaved={isSaved} message={saveMsg}
            selected={selected} onSave={save} onRestore={restore}
            onDelete={async (id) => {
              await profitability.remove(id)
              setSavedSig(null)
              setSelected('')
              loadScenarios()
            }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="axis-title">Show</span>
            <Segmented<Period> value={period} onChange={setPeriod} options={[
              { value: 'day', label: 'Daily' },
              { value: 'month', label: 'Monthly' },
            ]} />
          </div>
        </div>
      </div>

      <main style={MAIN_STYLE}>
        {err ? <ErrorBox error={err} /> : null}
        {!R_ ? <Loading what="the numbers" /> : (
          <>
            <KpiRow items={[
              { label: `Profit ${per}`, value: big(R_.profit * scale),
                tone: R_.profit < 0 ? 'bad' : 'good',
                note: R_.profit < 0 ? 'losing money at this base case' : 'after cotton and allocated overhead' },
              { label: 'Margin', value: pct(R_.margin), tone: R_.profit < 0 ? 'bad' : 'good',
                note: 'profit as a share of revenue' },
              { label: `Revenue ${per}`, value: big(R_.revenue * scale),
                note: isMix ? `${products.length} products running` : `${inr(rInputs.yarn)} per kg sold` },
              { label: `Cost ${per}`, value: big(R_.cost * scale),
                note: `${inr(R_.cotton_per_kg)} cotton per kg of yarn` },
              { label: `Production ${per}`, value: num(R_.prod * scale), unit: 'kg',
                note: `${num(R_.machines)} machines · ${num(R_.spindles)} spindles` },
              { label: 'Breakeven output',
                value: R_.breakeven ? num(R_.breakeven * scale) + ' kg' : 'never',
                tone: R_.breakeven && R_.prod < R_.breakeven ? 'bad' : 'good',
                note: R_.breakeven
                  ? (R_.prod > R_.breakeven
                    ? `${Math.round((R_.prod / R_.breakeven - 1) * 100)}% above breakeven`
                    : 'running below breakeven')
                  : 'cotton costs more than the yarn sells for' },
            ]} />

            {isMix ? (
              <>
                <CottonPanel
                  lots={lots} products={rProducts} market={market}
                  candyKg={base.data.constants.candy_kg}
                  setLot={setLot}
                  addLot={() => {
                    // The next grade the mix is not already following, so
                    // "Add cotton" adds a different cotton.
                    const used = new Set(lots.map((l) => l.grade))
                    const g = market?.cotton.grades.find((x) => !used.has(x.key))
                    const id = 'c' + (Math.max(0, ...lots.map((l) => Number(l.id.slice(1)) || 0)) + 1)
                    setLots([...lots, g
                      ? { id, grade: g.key, auto: true, candy: g.candy }
                      : { id, grade: null, auto: false, candy: 62000 }])
                  }}
                  removeLot={(id) => setLots(lots.filter((l) => l.id !== id))}
                />
                <MixEditor
                  products={products} resolved={rProducts} lots={lots} market={market}
                  result={R_} util={util} eff={eff}
                  setProduct={setProduct} pickCount={pickCount}
                  setUtil={setUtil} setEff={setEff}
                  reloadMarket={mkt.reload}
                  addProduct={() => {
                    // A new row starts as the first row's count, on its
                    // cotton — a real count with real prices, to be changed.
                    const like = products[0]
                    setProducts([...products, {
                      id: 'p' + seq, name: like?.name ?? 'New count',
                      count: like?.count, quality: like?.quality,
                      yarnAuto: like?.yarnAuto ?? false, cotton: like?.cotton ?? lots[0]?.id,
                      unit: 1, mcs: 1, yarn: like?.yarn ?? 245, candy: like?.candy ?? 62000,
                      real: 85, gps: like?.gps ?? 150,
                    }])
                    setSeq(seq + 1)
                  }}
                  removeProduct={(id) => setProducts(products.filter((p) => p.id !== id))}
                  reset={() => {
                    const mix = seedMix(base.data!.mix, market)
                    setProducts(mix.products)
                    setLots(mix.lots)
                    setUtil(base.data!.inputs.util)
                    setEff(base.data!.inputs.eff)
                  }}
                />
              </>
            ) : (
              // `oh` is omitted: overhead is entered by head in its own panel
              // below. It stays a sweepable driver, just not a second control
              // that would disagree with the heads.
              <BaseCase vars={base.data.vars.filter((v) => v.id !== 'oh')}
                inputs={rInputs} setVar={setVar}
                single={single} setSingle={setSingle} market={market}
                reloadMarket={mkt.reload}
                reset={() => {
                  setInputs(base.data!.inputs)
                  setSingleState(seedSingle(base.data!.single, market))
                }} />
            )}

            <OverheadHeads oh={out!.overhead} parts={ohParts}
              setPart={(id, v) => setOhParts((c) => ({ ...(c ?? {}), [id]: v }))}
              reset={() => setOhParts(Object.fromEntries(
                base.data!.overhead_heads.map((h) => [h.id, h.value])))} />

            {/* The economics restated against a kilo of yarn. It closes the
                inputs rather than opening them: every number in it is settled
                by the base case above and the overhead heads it opens into,
                so it reads as the answer to the two panels above it. */}
            <PerCotton pc={out!.per_cotton} oh={out!.overhead} isMix={isMix} />

            <section style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(420px,1fr))', gap: 24,
            }}>
              <Blueprint className="sheet">
                <PanelHead title="What moves profit" />
                <Note>
                  {isMix
                    ? 'Each bar sweeps one input — plant-wide or for a single product — across its range, holding everything else at the base case. The tick is where the base case sits.'
                    : 'Each bar sweeps one input across its full range with everything else held at the base case. The tick is where the base case sits.'}
                </Note>
                <Tornado rows={out!.tornado} base={R_.profit * scale} />
              </Blueprint>

              <Blueprint className="sheet">
                <PanelHead title="Sweep one variable" right={
                  <select className="no-print" value={out!.sweep.key} style={{ maxWidth: 230 }}
                    onChange={(e) => setAxis(e.target.value)}>
                    {out!.axes.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>
                } />
                <Note>
                  {out!.sweep.label} swept from {byFmt(out!.sweep.fmt, out!.sweep.min)} to{' '}
                  {byFmt(out!.sweep.fmt, out!.sweep.max)}. The dashed rule is the base case at{' '}
                  {byFmt(out!.sweep.fmt, out!.sweep.current)}
                  {out!.sweep.zero_crossing !== null
                    ? `; profit crosses zero at ${byFmt(out!.sweep.fmt, out!.sweep.zero_crossing)}.`
                    : '.'}
                </Note>
                <Sweep sweep={out!.sweep} />
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginTop: 12,
                  fontSize: 12, color: 'var(--color-neutral-700)',
                }}>
                  <LegendDot color="var(--c-bar4)" label="Revenue" />
                  <LegendDot color="var(--c-bar2)" label="Cost" />
                  <LegendDot color="var(--color-accent-900)" label="Profit" />
                  <span style={{ marginLeft: 'auto' }}>
                    {R_.breakeven ? `Breakeven ${num(R_.breakeven)} kg/day` : 'No breakeven at this cotton price'}
                  </span>
                </div>
              </Blueprint>
            </section>

            {!isMix && out!.ledger ? <Ledger rows={out!.ledger} /> : null}

            {/* The full rupee ledger by count closes the page: it is the
                detail you drop into once the per-kilo view has told you
                which count to look at. */}
            {isMix ? <MixLedger result={R_} scale={scale} period={period} /> : null}

            <footer className="pretty" style={{
              fontSize: 12, color: 'var(--color-neutral-600)',
              borderTop: '1px solid var(--color-divider)', paddingTop: 14, maxWidth: 900,
            }}>
              Cotton realization is yarn output as a share of cotton consumed.
              Plant overhead is built from six heads for the full 26-machine plant and
              prorated to the machines running; in product mix it is allocated to each
              product by its machine share. Output is standard capacity × utilization ×
              efficiency: utilization is the share of installed machine hours actually run,
              efficiency is output against standard while running.
              Output per spindle is per 8-hour shift over three shifts. Monthly figures scale
              the day by 30. Sensitivity sweeps move one input across its range and hold the
              rest at the base case. One candy is {out!.candy_kg} kg of lint, matching the
              rate tracker.
            </footer>
          </>
        )}
      </main>
    </>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 16, height: 3, background: color, display: 'inline-block' }} />
      {label}
    </span>
  )
}

// -------------------------------------------------------------- base case
function BaseCase({ vars, inputs, setVar, single, setSingle, market, reloadMarket, reset }: {
  vars: VarMeta[]
  /** As resolved: the two prices are the market's while they follow it. */
  inputs: ProfitInputs
  setVar: (id: keyof ProfitInputs, v: number) => void
  single: SinglePricing
  setSingle: (patch: Partial<SinglePricing>) => void
  market: Market | null
  reloadMarket: () => void
  reset: () => void
}) {
  return (
    <Blueprint className="sheet no-print" style={{ padding: '20px 22px' }}>
      <PanelHead title="Base case" right={
        <button className="btn btn-secondary" onClick={reset}>Reset to plant defaults</button>
      } />
      <SinglePrices single={single} setSingle={setSingle} market={market}
        yarn={inputs.yarn} candy={inputs.candy}
        gps={inputs.gps} useGps={(v) => setVar('gps', v)} />
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
        gap: '22px 34px', marginTop: 16,
      }}>
        {vars.map((m) => (
          <Slider key={m.id} label={m.label} min={m.min} max={m.max} step={m.step}
            value={inputs[m.id]} display={byFmt(m.fmt, inputs[m.id])} note={m.note}
            onChange={(v) => setVar(m.id, v)} />
        ))}
      </div>
      <MarketLine market={market} onLoaded={reloadMarket} />
    </Blueprint>
  )
}

// ------------------------------------------------------------ product mix
function MixEditor({
  products, resolved, lots, market, result, util, eff, setProduct, pickCount,
  setUtil, setEff, reloadMarket, addProduct, removeProduct, reset,
}: {
  products: Product[]
  /** The same rows carrying the prices in force. */
  resolved: Product[]
  lots: CottonLot[]
  market: Market | null
  result: ComputeResponse['result']
  util: number
  eff: number
  setProduct: (id: string, patch: Partial<Product>) => void
  pickCount: (id: string, count: number, quality: string) => void
  reloadMarket: () => void
  setUtil: (v: number) => void
  setEff: (v: number) => void
  addProduct: () => void
  removeProduct: (id: string) => void
  reset: () => void
}) {
  const lineFor = useMemo(
    () => new Map((result.lines ?? []).map((l) => [l.id, l])), [result.lines])
  const priced = useMemo(() => new Map(resolved.map((p) => [p.id, p])), [resolved])

  return (
    <Blueprint className="sheet no-print" style={{ padding: '20px 22px' }}>
      <PanelHead title="Product mix" right={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={addProduct}>Add product</button>
          <button className="btn btn-secondary" onClick={reset}>Reset mix</button>
        </div>
      } />
      <Note>
        {result.over_allocated
          ? `Careful — ${num(result.machines)} machines allocated against a 26-machine plant. Overhead is still charged on 26.`
          : `${num(result.machines)} of 26 machines allocated. Each row runs its own count at that count's yarn price, on one of the cottons above; overhead is split by machine share.`}
      </Note>

      <div className="scroll-x">
        <table className="table" style={{ minWidth: 1230 }}>
          <thead>
            <tr>
              <th>Product</th><th>Unit</th><th style={R}>Machines</th>
              <th style={R}>Yarn ₹/kg</th><th>Cotton · ₹/candy</th>
              <th style={R}>Realization %</th><th style={R}>g/spindle/shift</th>
              <th style={R}>Output kg/day</th><th />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id}>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <CountSelect product={p} market={market}
                      onPick={(count, quality) => pickCount(p.id, count, quality)} />
                    <input type="text" value={p.name} style={{ width: 170 }}
                      onChange={(e) => setProduct(p.id, { name: e.target.value })} />
                  </div>
                </td>
                <td>
                  <button className="btn btn-secondary" style={{ fontSize: 13, padding: '5px 10px' }}
                    onClick={() => setProduct(p.id, { unit: p.unit === 1 ? 2 : 1 })}>
                    Unit {p.unit}
                  </button>
                </td>
                <NumCell value={p.mcs} min={0} max={13} step={1} width={70}
                  onChange={(v) => setProduct(p.id, { mcs: v })} />
                <YarnCell product={p} shown={priced.get(p.id)?.yarn ?? p.yarn}
                  market={market} setProduct={setProduct} />
                <CottonCell product={p} shown={priced.get(p.id)?.candy ?? p.candy}
                  lots={lots} market={market} setProduct={setProduct} />
                <NumCell value={p.real} min={50} max={99} step={1} width={78}
                  onChange={(v) => setProduct(p.id, { real: v })} />
                <GpsCell product={p} market={market} setProduct={setProduct} />
                <td className="num" style={{ ...R, fontWeight: 600 }}>
                  {kg(lineFor.get(p.id)?.prod ?? 0)}
                </td>
                <td style={R}>
                  <button className="btn btn-ghost" style={{ fontSize: 12 }}
                    onClick={() => removeProduct(p.id)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
        gap: '22px 34px', marginTop: 20,
      }}>
        <Slider label="Capacity utilization" min={10} max={100} step={5}
          value={util} display={util + '%'}
          note="Share of installed machine hours actually run"
          onChange={setUtil} />
        <Slider label="Efficiency" min={10} max={100} step={1}
          value={eff} display={eff + '%'}
          note="Output against standard while running"
          onChange={setEff} />
      </div>
      <MarketLine market={market} onLoaded={reloadMarket} />
    </Blueprint>
  )
}

function NumCell({ value, min, max, step, width, onChange }: {
  value: number; min?: number; max?: number; step: number; width: number
  onChange: (v: number) => void
}) {
  return (
    <td style={R}>
      <NumberField value={value} min={min} max={max} step={step} style={{ width }}
        onChange={onChange} onEmpty={() => onChange(0)} />
    </td>
  )
}

function MixLedger({ result, scale, period }: {
  result: ComputeResponse['result']; scale: number; period: Period
}) {
  const lines = result.lines ?? []
  const maxContrib = Math.max(...lines.map((l) => Math.abs(l.contribution)), 1)

  return (
    <Blueprint className="sheet">
      <PanelHead title="Product by product"
        hint={period === 'month' ? 'Figures per month (30 days)' : 'Figures per day'} />
      <div className="scroll-x" style={{ marginTop: 12 }}>
        <table className="table" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              <th>Product</th><th style={R}>Output kg</th><th style={R}>Revenue</th>
              <th style={R}>Cotton cost</th><th style={R}>Overhead</th>
              <th style={R}>Profit</th><th style={R}>Margin</th>
              <th style={{ width: 130 }}>Contribution / kg</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const w = Math.max(2, Math.min(35, (Math.abs(l.contribution) / maxContrib) * 35))
              return (
                <tr key={l.id}>
                  <td style={{ fontWeight: 600 }}>
                    {l.name}<span className="muted" style={{ fontWeight: 400 }}>
                      {' · '}{num(l.mcs)} mcs · Unit {l.unit}</span>
                  </td>
                  <td className="num" style={R}>{kg(l.prod * scale)}</td>
                  <td className="num" style={R}>{big(l.revenue * scale)}</td>
                  <td className="num" style={R}>{big(l.cotton * scale)}</td>
                  <td className="num" style={R}>{big(l.overhead * scale)}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{big(l.profit * scale)}</td>
                  <td className="num" style={R}>{pct(l.margin)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg viewBox="0 0 70 8" style={{ width: 70, height: 8, display: 'block', flex: 'none' }}>
                        <rect x="0" y="1" width="70" height="6" fill="var(--c-track)" />
                        <rect x={l.contribution >= 0 ? 35 : 35 - w} y="1" width={w} height="6"
                          fill={l.contribution >= 0 ? 'var(--eff-strong)' : 'var(--eff-weak)'} />
                      </svg>
                      <span className="num" style={{ fontSize: 13 }}>{inr(l.contribution)}/kg</span>
                    </div>
                  </td>
                </tr>
              )
            })}
            <tr className="total">
              <td>All products</td>
              <td className="num" style={R}>{kg(result.prod * scale)}</td>
              <td className="num" style={R}>{big(result.revenue * scale)}</td>
              <td className="num" style={R}>{big(result.cotton * scale)}</td>
              <td className="num" style={R}>{big(result.overhead_day * scale)}</td>
              <td className="num" style={R}>{big(result.profit * scale)}</td>
              <td className="num" style={R}>{pct(result.margin)}</td>
              <td className="num muted">{inr(result.contribution)}/kg average</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Blueprint>
  )
}

function Ledger({ rows }: { rows: NonNullable<ComputeResponse['ledger']> }) {
  const show = (v: number | null, kind: string) =>
    v === null ? '—' : kind === 'kg' ? kg(v) : big(v)

  return (
    <Blueprint className="sheet">
      <PanelHead title="The arithmetic" />
      <div className="scroll-x" style={{ marginTop: 10 }}>
        <table className="table" style={{ minWidth: 560 }}>
          <thead>
            <tr><th>Line</th><th style={R}>Per day</th><th style={R}>Per month</th><th>Basis</th></tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.label}>
                <td style={{ fontWeight: 600 }}>{l.label}</td>
                <td className="num" style={R}>{show(l.day, l.kind)}</td>
                <td className="num" style={R}>{show(l.month, l.kind)}</td>
                <td className="muted">{l.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Blueprint>
  )
}

/** The same economics stated per kilo of yarn produced.
 *
 *  Yarn out is what the mill sells and quotes in, so a rupee figure here
 *  sits directly against a customer's price. The cotton column stays
 *  alongside every line — cotton is what actually gets bought, and a
 *  purchase is argued on that — but yarn is what the sheet leads with. */
function PerCotton({ pc, oh, isMix }: {
  pc: ComputeResponse['per_cotton']
  oh: ComputeResponse['overhead']
  isMix: boolean
}) {
  // Overhead is the one line here that is a sum of unrelated things, so it
  // is the one worth opening. The rest are single quantities.
  const [openOverhead, setOpenOverhead] = useState(false)
  if (!pc?.yarn_kg) return null
  const R: React.CSSProperties = { textAlign: 'right' }
  // Two decimals: these are per-kilo figures in the tens of rupees, where
  // whole-rupee rounding hides the difference that matters.
  const money = (v: number | null) => (v == null ? '–' : inr(v, 2))

  return (
    <Blueprint className="sheet">
      <PanelHead title="Per kilo of yarn" hint="Independent of volume and period" />
      <Note>
        Every rupee below is per one kilo of yarn produced, with the cotton-side
        figure alongside. {num(pc.yarn_kg)} kg of yarn comes off per day at this
        base case, each kilo taking {pc.cotton_per_yarn_kg?.toFixed(3)} kg of
        cotton ({pc.realization?.toFixed(1)}% realization).
      </Note>
      <div className="scroll-x">
        <table className="table" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th>Line</th><th style={R}>Per kg yarn</th>
              <th style={R}>Per kg cotton</th><th>Basis</th>
            </tr>
          </thead>
          <tbody>
            {pc.rows.map((r) => {
              const isOverhead = r.label === 'Overhead cost' && oh?.rows?.length > 0
              return (
                <Fragment key={r.label}>
                  <tr
                    className={isOverhead ? 'clickable' : undefined}
                    onClick={isOverhead ? () => setOpenOverhead((v) => !v) : undefined}
                  >
                    <td style={{ fontWeight: 600 }}>
                      {isOverhead ? (
                        <span className="caret" aria-hidden="true">
                          {openOverhead ? '▼' : '▶'}
                        </span>
                      ) : null}
                      {r.label}
                    </td>
                    <td className="num" style={R}>
                      {r.unit === 'kg' ? `${r.yarn?.toFixed(3)} kg` : money(r.yarn)}
                    </td>
                    <td className="num" style={R}>
                      {r.unit === 'kg' ? `${r.cotton?.toFixed(2)} kg` : money(r.cotton)}
                    </td>
                    <td className="muted">
                      {isOverhead
                        ? `${r.note} — ${openOverhead ? 'hide' : 'show'} the ${oh.rows.length} heads`
                        : r.note}
                    </td>
                  </tr>

                  {isOverhead && openOverhead ? oh.rows.map((h) => (
                    <tr key={h.id}>
                      <td style={{ paddingLeft: 26, fontWeight: 400 }}>
                        {h.label}
                        <div className="muted" style={{ fontSize: 11 }}>{h.note}</div>
                      </td>
                      <td className="num muted" style={R}>{money(h.per_kg)}</td>
                      <td className="num muted" style={R}>{money(h.per_kg_cotton)}</td>
                      <td className="muted" style={{ fontSize: 11 }}>
                        {crore(h.monthly)} a month
                      </td>
                    </tr>
                  )) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {isMix && pc.lines.length ? (
        <div className="scroll-x" style={{ marginTop: 18 }}>
          <table className="table" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>Product</th><th style={R}>Cotton / kg yarn</th><th style={R}>Cotton</th>
                <th style={R}>Revenue</th><th style={R}>Contribution</th>
                <th style={R}>Overhead</th><th style={R}>Profit</th>
              </tr>
            </thead>
            <tbody>
              {pc.lines.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontWeight: 600 }}>
                    {l.name}
                    <div className="muted" style={{ fontSize: 11, fontWeight: 400 }}>{l.meta}</div>
                  </td>
                  <td className="num" style={R}>{l.cotton_per_yarn_kg?.toFixed(3) ?? '–'} kg</td>
                  <td className="num" style={R}>{money(l.cotton)}</td>
                  <td className="num" style={R}>{money(l.revenue)}</td>
                  <td className="num" style={R}>{money(l.contribution)}</td>
                  <td className="num" style={R}>{money(l.overhead)}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{money(l.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Blueprint>
  )
}

/** Overhead by head. It was one 4.5 Cr figure, which made the biggest cost on
 *  the sheet the least examinable — movable, but not answerable. The per-kg
 *  column is the one that decides anything: a head is only large against the
 *  yarn it has to be recovered from. */
function OverheadHeads({ oh, parts, setPart, reset }: {
  oh: ComputeResponse['overhead']
  parts: Record<string, number> | null
  setPart: (id: string, value: number) => void
  reset: () => void
}) {
  if (!oh?.rows?.length) return null

  return (
    <Blueprint className="sheet">
      <PanelHead title="Plant overhead" right={
        <button className="btn btn-secondary" onClick={reset}>Reset overhead</button>
      } />
      <Note>
        Six heads for the full 26-machine plant, {crore(oh.total_monthly)} a month,
        prorated to the machines running — {inr(oh.allocated_day)} a day on
        {' '}{Math.round(oh.share * 100)}% of the plant. The per-kilo figure on each
        head is the one that decides anything: a head is only large against the
        yarn it has to be recovered from.
      </Note>
      <div className="field-grid">
        {oh.rows.map((r) => {
          const v = parts?.[r.id] ?? r.monthly
          return (
            <div className="field-card" key={r.id}>
              <div className="field-head">
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.label}</div>
                  <div className="muted" style={{ fontSize: 11 }}>{r.note}</div>
                </div>
                <NumberField className="num no-spin" min={0} max={r.max} step={100_000}
                  value={Math.round(v)}
                  onChange={(n) => setPart(r.id, Math.max(0, n))}
                  onEmpty={() => setPart(r.id, 0)}
                  style={{ width: 118, textAlign: 'right', flex: 'none' }} />
              </div>
              <input type="range" min={0} max={r.max} step={100_000} value={v}
                onChange={(e) => setPart(r.id, Number(e.target.value))} />
              <div className="field-foot">
                <span>{crore(v)} a month</span>
                <span>{inr(r.allocated_day)} a day</span>
                <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--color-text)' }}>
                  {r.per_kg == null ? '–' : inr(r.per_kg, 2)} / kg yarn
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '4px 26px', marginTop: 16, paddingTop: 10,
        borderTop: '2px solid var(--color-text)', fontSize: 13,
      }}>
        <span style={{ fontWeight: 600 }}>All heads</span>
        <span className="num">{crore(oh.total_monthly)} a month</span>
        <span className="num">{inr(oh.allocated_day)} a day allocated</span>
        <span className="num" style={{ marginLeft: 'auto', fontWeight: 600 }}>
          {oh.per_kg == null ? '–' : inr(oh.per_kg, 2)} / kg yarn
        </span>
      </div>
    </Blueprint>
  )
}

/** Save the current base case, and bring an old one back.
 *
 *  The design keeps one scenario in localStorage; these are named and live
 *  on the server, so a base case argued over on one machine is still there
 *  on another. The button states whether anything has changed since the
 *  last save rather than always offering to save. */
function ScenarioBar({ scenarios, isSaved, message, selected, onSave, onRestore, onDelete }: {
  scenarios: Scenario[]
  isSaved: boolean
  message: string
  selected: string
  onSave: () => void
  onRestore: (s: Scenario) => void
  onDelete: (id: number) => void
}) {
  const current = scenarios.find((sc) => sc.name === selected)
  return (
    <div className="no-print" style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    }}>
      <button className={`btn ${isSaved ? 'btn-secondary' : 'btn-primary'}`} onClick={onSave}>
        {isSaved ? 'Saved' : 'Save base case'}
      </button>

      {scenarios.length ? (
        <>
          <select
            value={selected}
            aria-label="Load a saved case"
            onChange={(e) => {
              const sc = scenarios.find((x) => x.name === e.target.value)
              if (sc) onRestore(sc)
            }}
            style={{ minWidth: 150 }}
          >
            <option value="" disabled>Load a case…</option>
            {scenarios.map((sc) => (
              <option key={sc.id} value={sc.name}>
                {sc.name} · {sc.mode === 'mix' ? 'mix' : 'single'}
              </option>
            ))}
          </select>
          {current ? (
            <button className="btn btn-ghost" style={{ fontSize: 12 }}
              onClick={() => onDelete(current.id)}>
              Delete
            </button>
          ) : null}
        </>
      ) : null}

      {message ? (
        <span className="field-note" style={{ color: 'var(--color-accent-700)' }}>{message}</span>
      ) : null}
    </div>
  )
}
